import type {
  BulkCreateResponse,
  CompanyInsightsAiSummaryResponse,
  CompanyInsightsResponse,
  CreateContactDto,
  EmailDraftInput,
  EmailDraftResponse,
  ExternalEmailDraftResponse,
  ExternalSuggestionsResponse,
  ImportLinkedinCsvResponse,
  NetworkApi,
  NetworkContactPatch,
  NetworkContactsResponse,
  NetworkContactView,
  NetworkListQuery,
  NetworkRecommendationsResponse,
} from "@/features/network/contracts";
import { httpClient } from "@/shared/api/httpClient";

function buildQuery(q?: NetworkListQuery): string {
  const params = new URLSearchParams();
  // Defaults match the connection guide's recommended page-level invocation:
  // hideTransactional=true drops billing/ticketing noise.
  const hideTransactional = q?.hideTransactional ?? true;
  const hideCurrentColleagues = q?.hideCurrentColleagues ?? false;
  params.set("hideTransactional", String(hideTransactional));
  params.set("hideCurrentColleagues", String(hideCurrentColleagues));
  if (q?.tier !== undefined) params.set("tier", String(q.tier));
  params.set("limit", String(q?.limit ?? 200));
  if (q?.offset !== undefined) params.set("offset", String(q.offset));
  return params.toString();
}

export const realNetworkApi: NetworkApi = {
  async list(query?: NetworkListQuery): Promise<NetworkContactsResponse> {
    const qs = buildQuery(query);
    return httpClient.get<NetworkContactsResponse>(`/api/network/contacts?${qs}`);
  },

  async recommendations(limit = 10): Promise<NetworkRecommendationsResponse> {
    return httpClient.get<NetworkRecommendationsResponse>(
      `/api/network/recommendations?limit=${encodeURIComponent(String(limit))}`,
    );
  },

  async getContactDrawer(contactId: string) {
    return httpClient.get<{
      id: string;
      primaryEmail: string | null;
      linkedinUrl: string | null;
    }>(`/api/network/contacts/${encodeURIComponent(contactId)}/drawer`);
  },

  // External-network suggestions (RocketReach-sourced people outside the user's
  // contacts). Read-only + cheap: this never spends an email-lookup credit.
  // Returns { items: [], enabled: false } when the feature flag is off or
  // RocketReach isn't configured - callers hide the widget.
  async externalSuggestions(limit = 10): Promise<ExternalSuggestionsResponse> {
    return httpClient.get<ExternalSuggestionsResponse>(
      `/api/network/external-suggestions?limit=${encodeURIComponent(String(limit))}`,
    );
  },

  // Company-scoped variant for the Pathways canvas - same cache/gate/id-space
  // as externalSuggestions above, just filtered (and, if stale, refreshed) for
  // one company.
  async externalSuggestionsForCompany(companyName: string, limit = 3): Promise<ExternalSuggestionsResponse> {
    return httpClient.get<ExternalSuggestionsResponse>(
      `/api/network/external-suggestions/company/${encodeURIComponent(companyName)}?limit=${encodeURIComponent(String(limit))}`,
    );
  },

  // Resolve the prospect's email (one on-demand RocketReach lookup) and
  // AI-generate a cold-outreach draft. 503 when the feature flag is off; the
  // resolved recipient may be null (LinkedIn fallback) even on success.
  async generateExternalEmailDraft(id: string, intent?: string): Promise<ExternalEmailDraftResponse> {
    return httpClient.post<ExternalEmailDraftResponse>(
      `/api/network/external-suggestions/${encodeURIComponent(id)}/email-draft`,
      { intent: intent ?? "" } as unknown as Record<string, unknown>,
    );
  },

  // Permanently exclude a prospect from external suggestions. Both are
  // idempotent no-ops server-side if already excluded; the widget refetches
  // afterward and backfills the next-ranked prospect.
  async markProspectReachedOut(id: string): Promise<{ ok: boolean; contactId: string | null; created: boolean }> {
    return httpClient.post<{ ok: boolean; contactId: string | null; created: boolean }>(
      `/api/network/external-suggestions/${encodeURIComponent(id)}/reached-out`,
    );
  },

  async dismissProspect(id: string): Promise<{ ok: boolean }> {
    return httpClient.post<{ ok: boolean }>(
      `/api/network/external-suggestions/${encodeURIComponent(id)}/dismiss`,
    );
  },

  async companyInsights(): Promise<CompanyInsightsResponse> {
    return httpClient.get<CompanyInsightsResponse>("/api/network/company-insights");
  },

  // On-demand only - invoked from the "Generate AI summary" button, never on
  // page load. No request body; the backend derives everything server-side.
  async aiSummary(): Promise<CompanyInsightsAiSummaryResponse> {
    return httpClient.post<CompanyInsightsAiSummaryResponse>(
      "/api/network/company-insights/ai-summary",
    );
  },

  async create(dto: CreateContactDto): Promise<NetworkContactView> {
    return httpClient.post<NetworkContactView>(
      "/api/network/contacts",
      dto as unknown as Record<string, unknown>,
    );
  },

  async bulkCreate(dtos: CreateContactDto[]): Promise<BulkCreateResponse> {
    return httpClient.post<BulkCreateResponse>(
      "/api/network/contacts/bulk",
      { contacts: dtos } as unknown as Record<string, unknown>,
    );
  },

  async importLinkedinCsv(file: File): Promise<ImportLinkedinCsvResponse> {
    const formData = new FormData();
    formData.append("file", file);
    return httpClient.postForm<ImportLinkedinCsvResponse>(
      "/api/onboarding-v2/network/linkedin-csv",
      formData,
    );
  },

  // AI-generate an outreach subject/body for a contact. Generation only -
  // the backend never sends or creates a Gmail draft. 503 when the
  // NETWORK_OUTREACH_AI_ENABLED flag is off; 400 on missing intent / bad email.
  async generateEmailDraft(input: EmailDraftInput): Promise<EmailDraftResponse> {
    return httpClient.post<EmailDraftResponse>(
      "/api/network/outreach/email-draft",
      input as unknown as Record<string, unknown>,
    );
  },

  async update(id: string, patch: NetworkContactPatch): Promise<NetworkContactView> {
    // The guide's PATCH field map allows passthrough of all the keys defined in
    // NetworkContactPatch. Backend's @IsIn validator rejects unknown contactType
    // values - keep that off the patch surface for now (UI doesn't send it).
    return httpClient.patch<NetworkContactView>(
      `/api/network/contacts/${encodeURIComponent(id)}`,
      patch as unknown as Record<string, unknown>,
    );
  },
};
